import React from "react";
import {
    Paper,
    Grid,
    ListItem,
    IconButton,
    ListItemAvatar,
    ListItemText,
    Stack,
    Divider,
    Box,
    Typography,
    Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import AirplanemodeActiveIcon from "@mui/icons-material/AirplanemodeActive";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import CheckIcon from "@mui/icons-material/Check";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import SpeakerNotesTwoToneIcon from "@mui/icons-material/SpeakerNotesTwoTone";
import CalendarDateIcon from "./Icons/CalendarDateIcon";
import NightsStayedIcon from "./Icons/NightsStayedIcon";

function DeletePerDiemModal(props) {
    const {
        deletePerDiem,
        perDiem,
        openDelete,
        setOpenDelete,
        darkState,
    } = props;

    const handleClose = () => {
        setOpenDelete(false);
    };

    const handleDelete = () => {
        deletePerDiem(perDiem.id);
        setOpenDelete(false);
    };

    return (
        <div>
            <Dialog
                open={openDelete}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    <Alert severity="error">
                        Permanently delete this per diem?
                    </Alert>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {moment(perDiem.start_date).format("MMM Do")} -{" "}
                        {moment(perDiem.end_date).format("MMM Do YYYY")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handleClose}>
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        autoFocus
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default function PerDiemCard(props) {
    const {
        darkState,
        perDiem,
        setOpenPerDiem,
        setEditing,
        setEditPerDiem,
        user,
        deletePerDiem,
        approvePerDiem,
        isAdmin,
    } = props;
    const [openDelete, setOpenDelete] = React.useState(false);
    const [openProcessedInfo, setOpenProcessedInfo] = React.useState(false);
    const cardIsEditable = !perDiem.is_approved;

    const handleEdit = () => {
        if (!cardIsEditable) {
            return;
        }
        setEditPerDiem(perDiem);
        setEditing(true);
        setOpenPerDiem(true);
    };

    const handleDelete = (event) => {
        if (event && typeof event.stopPropagation === "function") {
            event.stopPropagation();
        }
        setOpenDelete(true);
    };

    const handleApprove = () => {
        approvePerDiem(perDiem.id);
    };

    const handleShowProcessedInfo = () => {
        setOpenProcessedInfo(true);
    };

    const handleCloseProcessedInfo = () => {
        setOpenProcessedInfo(false);
    };

    const handleCardClick = () => {
        if (cardIsEditable) {
            handleEdit();
        } else {
            handleShowProcessedInfo();
        }
    };

    const handleCardKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (cardIsEditable) {
                handleEdit();
            } else {
                handleShowProcessedInfo();
            }
        }
    };

    // Calculate total amount based on total days (inclusive) and rate
    const startDate = moment(perDiem.start_date);
    const endDate = moment(perDiem.end_date);
    const nights = Math.max(endDate.diff(startDate, "days"), 0);
    const rateAmount =
        perDiem.rate && perDiem.rate.amount != null
            ? perDiem.rate.amount
            : 45.0; // fallback to current rate
    const totalAmount = nights * parseFloat(rateAmount);
    const isoWeekLabel =
        perDiem.isoweek && typeof perDiem.isoweek === "string"
            ? perDiem.isoweek.toUpperCase()
            : startDate.format("GGGG[W]WW");
    const weekRangeLabel = `${startDate.format("MMM D")} - ${endDate.format(
        "MMM D"
    )}`;

    const userName =
        perDiem.user && typeof perDiem.user === "object"
            ? `${perDiem.user.first_name || ""} ${perDiem.user.last_name ||
                  ""}`.trim()
            : "";
    const statusText = perDiem.is_paid
        ? perDiem.is_approved
            ? "Approved & Paid"
            : "Paid"
        : perDiem.is_approved
        ? "Approved"
        : "Pending";

    const statusDisplay = isAdmin ? (
        perDiem.is_approved && !perDiem.is_paid ? (
            <Button
                variant="outlined"
                startIcon={<CheckBoxIcon />}
                color="success"
                size="small"
                onClick={(event) => {
                    event.stopPropagation();
                    handleApprove();
                }}
            >
                Approved
            </Button>
        ) : perDiem.is_paid ? (
            <Button
                variant="outlined"
                startIcon={<AttachMoneyIcon />}
                color="inherit"
                sx={{
                    color: (theme) => theme.palette.grey[700],
                    borderColor: (theme) => theme.palette.grey[700],
                    "&:hover": {
                        borderColor: (theme) => theme.palette.grey[900],
                        backgroundColor: (theme) => theme.palette.grey[300],
                        color: (theme) => theme.palette.grey[900],
                    },
                }}
                size="small"
                onClick={(event) => event.stopPropagation()}
            >
                Paid
            </Button>
        ) : (
            <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={(event) => {
                    event.stopPropagation();
                    handleApprove();
                }}
            >
                Approve
            </Button>
        )
    ) : (
        <span>{statusText}</span>
    );

    return (
        <>
            <Paper
                variant="outlined"
                sx={{
                    my: 2,
                    width: "100%",
                    maxWidth: "500px",
                    border: darkState ? "1.5px solid" : "3px solid",
                    borderColor: "primary.main",
                    borderRadius: "16px",
                    position: "relative",
                    cursor: "pointer",
                    transition: "transform 0.2s ease-in-out",
                    "&:hover": {
                        transform: "translateY(-4px)",
                    },
                }}
                onClick={handleCardClick}
                onKeyDown={handleCardKeyDown}
                role="button"
                tabIndex={0}
            >
                {!perDiem.is_approved ? (
                    <>
                        <IconButton
                            // color="error"
                            aria-label="delete per diem"
                            onClick={handleDelete}
                            size="medium"
                            sx={{
                                position: "absolute",
                                top: { xs: 4, sm: 8 },
                                right: { xs: 4, sm: 8 },
                                zIndex: 2,
                            }}
                        >
                            <CloseIcon fontSize="medium" />
                        </IconButton>
                    </>
                ) : null}
                <Grid container wrap="nowrap" spacing={1}>
                    <Grid item xs zeroMinWidth>
                        <ListItem
                            sx={{
                                alignItems: "stretch",
                                pr: { xs: 6, sm: 2 }, // Add more right padding on small screens
                                pt: { xs: 5, sm: 1 }, // Add more top padding on small screens
                            }}
                        >
                            <ListItemAvatar
                                sx={{
                                    marginRight: "1rem",
                                    marginBottom: "1rem",
                                }}
                            >
                                <Chip
                                    sx={{
                                        marginTop: "1.5rem",
                                        marginBottom: "1.5rem",
                                    }}
                                    color={
                                        perDiem.is_paid
                                            ? "default"
                                            : perDiem.is_approved
                                            ? "success"
                                            : "primary"
                                    }
                                    icon={
                                        perDiem.is_paid ? (
                                            <AttachMoneyIcon />
                                        ) : perDiem.is_approved ? (
                                            <CheckIcon />
                                        ) : (
                                            <AirplanemodeActiveIcon />
                                        )
                                    }
                                    label={`$${totalAmount.toFixed(2)}`}
                                />
                                <Divider />
                                <ListItemText
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginTop: "1rem",
                                    }}
                                    primary={statusDisplay}
                                />
                            </ListItemAvatar>

                            <ListItemText
                                primary={
                                    <Box>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                            justifyContent="flex-start"
                                            sx={{
                                                fontWeight: 700,
                                                marginBottom: ".25rem",
                                                width: "100%",
                                            }}
                                        >
                                            <Stack
                                                spacing={0.5}
                                                alignItems="center"
                                            >
                                                <CalendarDateIcon
                                                    date={startDate}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    component="div"
                                                    color="text.secondary"
                                                >
                                                    Start
                                                </Typography>
                                            </Stack>
                                            <Divider
                                                orientation="vertical"
                                                flexItem
                                            />
                                            <Stack
                                                spacing={0.5}
                                                alignItems="center"
                                            >
                                                <NightsStayedIcon
                                                    nights={nights}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    component="div"
                                                    color="text.secondary"
                                                >
                                                    {nights === 1
                                                        ? "Night"
                                                        : "Nights"}
                                                </Typography>
                                            </Stack>
                                            <Divider
                                                orientation="vertical"
                                                flexItem
                                            />
                                            <Stack
                                                spacing={0.5}
                                                alignItems="center"
                                            >
                                                <CalendarDateIcon
                                                    date={endDate}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    component="div"
                                                    color="text.secondary"
                                                >
                                                    End
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                }
                                secondary={
                                    <>
                                        {userName ? (
                                            <>
                                                {userName}
                                                <br />
                                            </>
                                        ) : null}
                                        {`Week: ${isoWeekLabel}`}
                                        <br />
                                        {((perDiem &&
                                            perDiem.project &&
                                            perDiem.project.number) ||
                                            (perDiem &&
                                                perDiem.service &&
                                                perDiem.service.number) ||
                                            (perDiem &&
                                                perDiem.hse &&
                                                perDiem.hse.number)) && (
                                            <>
                                                {`Project: ${(perDiem &&
                                                    perDiem.project &&
                                                    perDiem.project.number) ||
                                                    (perDiem &&
                                                        perDiem.service &&
                                                        perDiem.service
                                                            .number) ||
                                                    (perDiem &&
                                                        perDiem.hse &&
                                                        perDiem.hse.number)}`}
                                                <br />
                                            </>
                                        )}
                                        {`Rate: $${parseFloat(
                                            rateAmount
                                        ).toFixed(2)}/night`}
                                        <br />
                                        {`Status: ${statusText}`}
                                    </>
                                }
                            />
                        </ListItem>
                    </Grid>
                </Grid>
                {perDiem.notes ? (
                    <Tooltip title={perDiem.notes} enterTouchDelay={0}>
                        <IconButton
                            size="small"
                            aria-label="notes"
                            sx={{
                                position: "absolute",
                                right: 12,
                                bottom: 12,
                            }}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <SpeakerNotesTwoToneIcon />
                        </IconButton>
                    </Tooltip>
                ) : null}
            </Paper>
            <DeletePerDiemModal
                deletePerDiem={deletePerDiem}
                perDiem={perDiem}
                openDelete={openDelete}
                setOpenDelete={setOpenDelete}
            />
            <Dialog
                open={openProcessedInfo}
                onClose={handleCloseProcessedInfo}
                aria-labelledby="processed-dialog-title"
            >
                <DialogTitle id="processed-dialog-title">
                    Already Processed
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This per diem has already been processed. Contact your
                        administrator if you need to request any changes.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseProcessedInfo} autoFocus>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
